import { useController } from 'react-hook-form';
import { createContext, useContext, cloneElement } from 'react';

const FormErrorProviderContext = createContext({
  onError: (error) => error?.message,
});

export function FormErrorProvider({ onError, children }) {
  return (
    <FormErrorProviderContext.Provider value={{ onError }}>
      {children}
    </FormErrorProviderContext.Provider>
  );
}

export const useFormError = () => {
  const errorCtx = useContext(FormErrorProviderContext);
  return errorCtx?.onError;
};

const FormControl = ({
  name,
  rules = {},
  required,
  children,
  parseError,
  normalize,
  valuePropName = 'value',
  ...rest
}) => {
  const finalRules = {
    ...rules,
    ...(required &&
      !rules.required && {
        required:
          typeof required === 'string' ? required : 'This field is required',
      }),
    ...(rest.type === 'email' &&
      !rules.pattern && {
        pattern: {
          value:
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
          message: 'Please enter a valid email address',
        },
      }),
  };

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    rules: finalRules,
    disabled: rest.disabled,
  });

  return cloneElement(children, {
    ...rest,
    ref: field.ref,
    name: field.name,
    [valuePropName]: field.value,
    hasError: !!error,
    helpText: error
      ? typeof parseError === 'function'
        ? parseError(error)
        : error.message
      : rest.helperText,
    onBlur: (e) => {
      field.onBlur(e);
      if (rest.onBlur) rest.onBlur(e);
    },
    onChange: (e) => {
      // Apply normalization if a normalize function is provided
      if (typeof normalize === 'function') {
        const value = e?.target?.value !== undefined ? e.target.value : e;
        const normalizedValue = normalize(value);
        field.onChange(normalizedValue);
      } else {
        field.onChange(e);
      }

      if (rest.onChange) rest.onChange(e);
    },
  });
};

export default FormControl;
